import React, { useState, useEffect } from 'react';
import api from '../api';
import toast, { Toaster } from 'react-hot-toast';
import ExportButton from '../components/ExportButton';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
import { parseServerDate, todayLocalStr, firstOfMonthLocalStr } from '../utils/dateUtils';

export default function Income() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState(firstOfMonthLocalStr());
  const [endDate, setEndDate] = useState(todayLocalStr());
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [acctId, setAcctId] = useState('');
  const [amount, setAmount] = useState('');
  const [desc, setDesc] = useState('');
  const [incDate, setIncDate] = useState(todayLocalStr());
  const [loading, setLoading] = useState(false);
  // Click-to-sort, as requested.
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });

  useEffect(() => {
    fetchData();
    api.get('/customers').then(r => setCustomers(r.data));
    api.get('/auth/users').then(r => setUsers(r.data));
    api.get('/accounts').then(r => setAccounts(r.data));
  }, []);

  const fetchData = () => {
    setLoading(true);
    const start = startDate, end = endDate;
    Promise.all([
      api.get('/income?startDate=' + start + '&endDate=' + end),
      api.get('/capital-repayments?start=' + start + '&end=' + end)
    ]).then(([incRes, capRes]) => {
      // Combine income entries + capital repayments
      const incomeEntries = incRes.data.map(e => ({ ...e, entry_type: 'income' }));
      const capEntries = capRes.data.map(e => ({ ...e, entry_type: 'capital_repay', income_type: 'capital_repayment' }));
      let all = [...incomeEntries, ...capEntries];
      
      if (filterCustomer) all = all.filter(e => e.customer_name === filterCustomer);
      if (filterUser) all = all.filter(e => (e.description || '').toLowerCase().includes(filterUser.toLowerCase()));
      if (filterType) all = all.filter(e => e.income_type === filterType);
      
      // Sort by created_at descending
      all.sort((a, b) => parseServerDate(b.created_at || b.income_date) - parseServerDate(a.created_at || a.income_date));
      
      setEntries(all);
      setLoading(false);
      
      // Calculate summary
      const incAdv = all.filter(e => e.income_type === 'interest_advance').reduce((s, e) => s + e.amount, 0);
      const incColl = all.filter(e => e.income_type === 'interest_collection').reduce((s, e) => s + e.amount, 0);
      const other = all.filter(e => e.income_type === 'other').reduce((s, e) => s + e.amount, 0);
      const cap = all.filter(e => e.income_type === 'capital_repayment').reduce((s, e) => s + e.amount, 0);
      const totalIncome = incAdv + incColl;
      setSummary({ interest_advance: incAdv, interest_collection: incColl, other_income: other, capital_repayment: cap, total_income: totalIncome });
    });
  };

  const addIncome = (e) => {
    e.preventDefault();
    if (!acctId || !amount) return toast.error('Fill required fields');
    api.post('/income', { account_id: parseInt(acctId), amount: parseFloat(amount), description: desc, income_date: incDate }).then(() => {
      toast.success('Income recorded!');
      setShowForm(false); setAcctId(''); setAmount(''); setDesc(''); fetchData();
    }).catch(err => toast.error(err.response?.data?.error));
  };

  const fmt = (n) => '₹' + (n || 0).toLocaleString('en-IN');

  function handleSort(key) {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  }
  function sortIndicator(key) {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
  }
  const sortedEntries = entries.slice().sort((a, b) => {
    let av, bv;
    if (sort.key === 'created_at') { av = parseServerDate(a.created_at || a.income_date).getTime(); bv = parseServerDate(b.created_at || b.income_date).getTime(); }
    else if (sort.key === 'amount') { av = a.amount || 0; bv = b.amount || 0; }
    else if (sort.key === 'collected_by') {
      av = ((a.description || '').includes('Collected by') ? (a.description || '').replace('Collected by ', '') : (a.collected_by || '')).toLowerCase();
      bv = ((b.description || '').includes('Collected by') ? (b.description || '').replace('Collected by ', '') : (b.collected_by || '')).toLowerCase();
    } else { av = (a[sort.key] || '').toString().toLowerCase(); bv = (b[sort.key] || '').toString().toLowerCase(); }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <Toaster position="top-right" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 'bold', margin: 0 }}>Payment Report</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowForm(true)} style={{ background: '#16a34a', color: 'white', padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer' }}>+ Add Income</button>
          <ExportButton apiUrl="/api/income" filename="payments.csv" />
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 8, padding: 12, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
        <div><label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 2 }}>From</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 2 }}>To</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 2 }}>Customer</label><CustomerAutocomplete customers={customers} selectedLabel={filterCustomer} placeholder="All (start typing to filter)" onSelect={c => setFilterCustomer(c.name)} onClear={() => setFilterCustomer('')} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 2 }}>Type</label><select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}><option value="">All Types</option><option value="interest_advance">Interest Advance</option><option value="interest_collection">Interest Collection</option><option value="capital_repayment">Capital Repayment</option><option value="other">Other Income</option></select></div>
        <div><label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 2 }}>Collected By</label><select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}><option value="">All</option>{users.map(u => <option key={u.id} value={u.full_name || u.username}>{u.full_name || u.username}</option>)}</select></div>
        <button onClick={fetchData} style={{ background: '#2563eb', color: 'white', border: 'none', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Apply</button>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
          <div style={{ background: '#dbeafe', borderRadius: 8, padding: 14 }}><p style={{ fontSize: 12, color: '#1e40af', margin: 0 }}>Interest Advance</p><p style={{ fontSize: 20, fontWeight: 'bold', color: '#1e3a8a', margin: '4px 0 0 0' }}>{fmt(summary.interest_advance)}</p></div>
          <div style={{ background: '#dcfce7', borderRadius: 8, padding: 14 }}><p style={{ fontSize: 12, color: '#16a34a', margin: 0 }}>Interest Collection</p><p style={{ fontSize: 20, fontWeight: 'bold', color: '#14532d', margin: '4px 0 0 0' }}>{fmt(summary.interest_collection)}</p></div>
          <div style={{ background: '#fef3c7', borderRadius: 8, padding: 14 }}><p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>Capital Repayment</p><p style={{ fontSize: 20, fontWeight: 'bold', color: '#78350f', margin: '4px 0 0 0' }}>{fmt(summary.capital_repayment)}</p></div>
          <div style={{ background: '#f3e8ff', borderRadius: 8, padding: 14 }}><p style={{ fontSize: 12, color: '#7c3aed', margin: 0 }}>Other Income</p><p style={{ fontSize: 20, fontWeight: 'bold', color: '#581c87', margin: '4px 0 0 0' }}>{fmt(summary.other_income)}</p></div>
          <div style={{ background: '#e0e7ff', borderRadius: 8, padding: 14 }}><p style={{ fontSize: 12, color: '#4338ca', margin: 0 }}>Total Income</p><p style={{ fontSize: 22, fontWeight: 'bold', color: '#312e81', margin: '4px 0 0 0' }}>{fmt(summary.total_income)}</p></div>
        </div>
      )}

      {loading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              {[
                { key: 'created_at', label: 'Date & Time' },
                { key: 'income_type', label: 'Type' },
                { key: 'description', label: 'Description' },
                { key: 'account_name', label: 'Account' },
                { key: 'customer_name', label: 'Customer' },
                { key: 'collected_by', label: 'Collected By' },
              ].map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 500, color: sort.key === col.key ? '#2563eb' : '#6b7280', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>{col.label}{sortIndicator(col.key)}</th>
              ))}
              <th onClick={() => handleSort('amount')} style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, fontWeight: 500, color: sort.key === 'amount' ? '#2563eb' : '#6b7280', cursor: 'pointer', userSelect: 'none' }}>Amount{sortIndicator('amount')}</th>
            </tr></thead>
            <tbody>
              {sortedEntries.map(e => {
                const typeColors = { interest_advance: { bg: '#dbeafe', color: '#1e40af' }, interest_collection: { bg: '#dcfce7', color: '#16a34a' }, capital_repayment: { bg: '#fef3c7', color: '#92400e' }, other: { bg: '#f3e8ff', color: '#7c3aed' } };
                const tc = typeColors[e.income_type] || { bg: '#f3f4f6', color: '#6b7280' };
                return (
                  <tr key={e.id + '_' + e.entry_type} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>{parseServerDate(e.created_at || e.income_date).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 12px' }}><span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500, background: tc.bg, color: tc.color }}>{(e.income_type || '').replace(/_/g, ' ').toUpperCase()}</span></td>
                    <td style={{ padding: '10px 12px' }}>{e.description || ''}</td>
                    <td style={{ padding: '10px 12px' }}>{e.account_name || ''}</td>
                    <td style={{ padding: '10px 12px' }}>{e.customer_name || ''}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{(e.description || '').includes('Collected by') ? (e.description || '').replace('Collected by ', '') : (e.collected_by || '')}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: e.income_type === 'capital_repayment' ? '#92400e' : '#16a34a' }}>{fmt(e.amount)}</td>
                  </tr>
                );
              })}
              {entries.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No entries found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Add Income</h3>
            <form onSubmit={addIncome}>
              <div style={{ marginBottom: 12 }}><label>Account *</label><select value={acctId} onChange={e => setAcctId(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}><option value="">Select</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
              <div style={{ marginBottom: 12 }}><label>Amount *</label><input type="number" value={amount} onChange={e => setAmount(e.target.value)} required min="1" style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
              <div style={{ marginBottom: 12 }}><label>Description</label><input type="text" value={desc} onChange={e => setDesc(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
              <div style={{ marginBottom: 12 }}><label>Date</label><input type="date" value={incDate} onChange={e => setIncDate(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} /></div>
              <div style={{ display: 'flex', gap: 8 }}><button type="submit" style={{ flex: 1, padding: 10, background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Save</button><button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 10, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}





